// Local UI state used by the claims-management page.
import { Dispatch, SetStateAction, useState } from 'react';
import {
  MRT_ExpandedState,
  MRT_RowSelectionState,
  MRT_TableInstance,
} from 'mantine-react-table';
import { CreateClaimForm, SharedClaimForm } from 'interfaces';
import { ClaimTableRow } from 'pages/claims/utils/claimTableRows';

export type ClaimCreateModalType = 'claim' | 'bulk' | 'repeat';

export interface ClaimQuickCreatePrefill {
  createInitialValues?: Partial<CreateClaimForm>;
  bulkSharedInitialValues?: Partial<SharedClaimForm>;
}

export interface ClaimContextMenuPosition {
  x: number;
  y: number;
}

export type ClaimRowModalProps = {
  historyOpen: boolean;
  debitOpen: boolean;
  submissionOpen: boolean;
  viewBatchOpen: boolean;
  invoiceOpen: boolean;
  cancelClaimOpen: boolean;
  queryModalOpen: boolean;
  invoiceModalOpen: boolean;
  resubmitOpen: boolean;
  reconcileOpen: boolean;
  matchOpen: boolean;
  writeOffOpen: boolean;
  markReadyOpen: boolean;
  markIncompleteOpen: boolean;
  markHistoricalOpen: boolean;
  markPaidOpen: boolean;
  markUninsuredOpen: boolean;
  markPendingWriteOffOpen: boolean;
  internalCommentOpen: boolean;
  receiptOpen: boolean;
  actionRequestOpen: boolean;
  viewActionsOpen: boolean;
  sourceDataOpen: boolean;
  attachmentsOpen: boolean;
  supportingDocumentOpen: boolean;
  setHistoryOpen: Dispatch<SetStateAction<boolean>>;
  setDebitOpen: Dispatch<SetStateAction<boolean>>;
  setSubmissionOpen: Dispatch<SetStateAction<boolean>>;
  setViewBatchOpen: Dispatch<SetStateAction<boolean>>;
  setInvoiceOpen: Dispatch<SetStateAction<boolean>>;
  setCancelClaimOpen: Dispatch<SetStateAction<boolean>>;
  setQueryModalOpen: Dispatch<SetStateAction<boolean>>;
  setInvoiceModalOpen: Dispatch<SetStateAction<boolean>>;
  setResubmitOpen: Dispatch<SetStateAction<boolean>>;
  setReconcileOpen: Dispatch<SetStateAction<boolean>>;
  setMatchOpen: Dispatch<SetStateAction<boolean>>;
  setWriteOffOpen: Dispatch<SetStateAction<boolean>>;
  setMarkReadyOpen: Dispatch<SetStateAction<boolean>>;
  setMarkIncompleteOpen: Dispatch<SetStateAction<boolean>>;
  setMarkHistoricalOpen: Dispatch<SetStateAction<boolean>>;
  setMarkPaidOpen: Dispatch<SetStateAction<boolean>>;
  setMarkUninsuredOpen: Dispatch<SetStateAction<boolean>>;
  setMarkPendingWriteOffOpen: Dispatch<SetStateAction<boolean>>;
  setInternalCommentOpen: Dispatch<SetStateAction<boolean>>;
  setReceiptOpen: Dispatch<SetStateAction<boolean>>;
  setActionRequestOpen: Dispatch<SetStateAction<boolean>>;
  setViewActionsOpen: Dispatch<SetStateAction<boolean>>;
  setSourceDataOpen: Dispatch<SetStateAction<boolean>>;
  setAttachmentsOpen: Dispatch<SetStateAction<boolean>>;
  setSupportingDocumentOpen: Dispatch<SetStateAction<boolean>>;
};
export const useClaimRowModalState = (): ClaimRowModalProps => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [debitOpen, setDebitOpen] = useState(false);
  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [viewBatchOpen, setViewBatchOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [cancelClaimOpen, setCancelClaimOpen] = useState(false);
  const [queryModalOpen, setQueryModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [markReadyOpen, setMarkReadyOpen] = useState(false);
  const [markIncompleteOpen, setMarkIncompleteOpen] = useState(false);
  const [markHistoricalOpen, setMarkHistoricalOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markUninsuredOpen, setMarkUninsuredOpen] = useState(false);
  const [markPendingWriteOffOpen, setMarkPendingWriteOffOpen] = useState(false);
  const [internalCommentOpen, setInternalCommentOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [actionRequestOpen, setActionRequestOpen] = useState(false);
  const [viewActionsOpen, setViewActionsOpen] = useState(false);
  const [sourceDataOpen, setSourceDataOpen] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [supportingDocumentOpen, setSupportingDocumentOpen] = useState(false);

  return {
    historyOpen,
    debitOpen,
    submissionOpen,
    viewBatchOpen,
    invoiceOpen,
    cancelClaimOpen,
    queryModalOpen,
    invoiceModalOpen,
    resubmitOpen,
    reconcileOpen,
    matchOpen,
    writeOffOpen,
    markReadyOpen,
    markIncompleteOpen,
    markHistoricalOpen,
    markPaidOpen,
    markUninsuredOpen,
    markPendingWriteOffOpen,
    internalCommentOpen,
    receiptOpen,
    actionRequestOpen,
    viewActionsOpen,
    sourceDataOpen,
    attachmentsOpen,
    supportingDocumentOpen,
    setHistoryOpen,
    setDebitOpen,
    setSubmissionOpen,
    setViewBatchOpen,
    setInvoiceOpen,
    setCancelClaimOpen,
    setQueryModalOpen,
    setInvoiceModalOpen,
    setResubmitOpen,
    setReconcileOpen,
    setMatchOpen,
    setWriteOffOpen,
    setMarkReadyOpen,
    setMarkIncompleteOpen,
    setMarkHistoricalOpen,
    setMarkPaidOpen,
    setMarkUninsuredOpen,
    setMarkPendingWriteOffOpen,
    setInternalCommentOpen,
    setReceiptOpen,
    setActionRequestOpen,
    setViewActionsOpen,
    setSourceDataOpen,
    setAttachmentsOpen,
    setSupportingDocumentOpen,
  };
};

interface UseClaimTableStateResult {
  manualLoading: boolean;
  setManualLoading: Dispatch<SetStateAction<boolean>>;
  expanded: MRT_ExpandedState;
  setExpanded: Dispatch<SetStateAction<MRT_ExpandedState>>;
  tableInstance: MRT_TableInstance<ClaimTableRow> | null;
  setTableInstance: Dispatch<
    SetStateAction<MRT_TableInstance<ClaimTableRow> | null>
  >;
  createModalType: ClaimCreateModalType;
  setCreateModalType: Dispatch<SetStateAction<ClaimCreateModalType>>;
  quickCreatePrefill: ClaimQuickCreatePrefill | null;
  setQuickCreatePrefill: Dispatch<
    SetStateAction<ClaimQuickCreatePrefill | null>
  >;
  useSelectedClaimForDuplicate: boolean;
  setUseSelectedClaimForDuplicate: Dispatch<SetStateAction<boolean>>;
  rowSelection: MRT_RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<MRT_RowSelectionState>>;
  contextMenuPosition: ClaimContextMenuPosition | null;
  setContextMenuPosition: Dispatch<
    SetStateAction<ClaimContextMenuPosition | null>
  >;
}

export const useClaimTableState = (): UseClaimTableStateResult => {
  const [manualLoading, setManualLoading] = useState(false);
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});
  const [tableInstance, setTableInstance] =
    useState<MRT_TableInstance<ClaimTableRow> | null>(null);
  const [createModalType, setCreateModalType] =
    useState<ClaimCreateModalType>('claim');
  const [quickCreatePrefill, setQuickCreatePrefill] =
    useState<ClaimQuickCreatePrefill | null>(null);
  const [useSelectedClaimForDuplicate, setUseSelectedClaimForDuplicate] =
    useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [contextMenuPosition, setContextMenuPosition] =
    useState<ClaimContextMenuPosition | null>(null);

  return {
    manualLoading,
    setManualLoading,
    expanded,
    setExpanded,
    tableInstance,
    setTableInstance,
    createModalType,
    setCreateModalType,
    quickCreatePrefill,
    setQuickCreatePrefill,
    useSelectedClaimForDuplicate,
    setUseSelectedClaimForDuplicate,
    rowSelection,
    setRowSelection,
    contextMenuPosition,
    setContextMenuPosition,
  };
};

interface UseClaimBulkStateResult {
  bulkResubmitOpen: boolean;
  setBulkResubmitOpen: Dispatch<SetStateAction<boolean>>;
  bulkResubmitRows: ClaimTableRow[];
  setBulkResubmitRows: Dispatch<SetStateAction<ClaimTableRow[]>>;
  bulkReadyAction: 'ready' | 'resubmit';
  setBulkReadyAction: Dispatch<SetStateAction<'ready' | 'resubmit'>>;
  bulkReconcileOpen: boolean;
  setBulkReconcileOpen: Dispatch<SetStateAction<boolean>>;
  bulkReconcileRows: ClaimTableRow[];
  setBulkReconcileRows: Dispatch<SetStateAction<ClaimTableRow[]>>;
  bulkCancelClaimIDs: string[];
  setBulkCancelClaimIDs: Dispatch<SetStateAction<string[]>>;
  bulkCancelOpen: boolean;
  setBulkCancelOpen: Dispatch<SetStateAction<boolean>>;
  bulkEditClaimIDs: string[];
  setBulkEditClaimIDs: Dispatch<SetStateAction<string[]>>;
  bulkEditOpen: boolean;
  setBulkEditOpen: Dispatch<SetStateAction<boolean>>;
}

export const useClaimBulkState = (): UseClaimBulkStateResult => {
  const [bulkResubmitOpen, setBulkResubmitOpen] = useState(false);
  const [bulkResubmitRows, setBulkResubmitRows] = useState<ClaimTableRow[]>([]);
  const [bulkReadyAction, setBulkReadyAction] = useState<'ready' | 'resubmit'>(
    'resubmit'
  );
  const [bulkReconcileOpen, setBulkReconcileOpen] = useState(false);
  const [bulkReconcileRows, setBulkReconcileRows] = useState<ClaimTableRow[]>(
    []
  );
  const [bulkCancelClaimIDs, setBulkCancelClaimIDs] = useState<string[]>([]);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkEditClaimIDs, setBulkEditClaimIDs] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  return {
    bulkResubmitOpen,
    setBulkResubmitOpen,
    bulkResubmitRows,
    setBulkResubmitRows,
    bulkReadyAction,
    setBulkReadyAction,
    bulkReconcileOpen,
    setBulkReconcileOpen,
    bulkReconcileRows,
    setBulkReconcileRows,
    bulkCancelClaimIDs,
    setBulkCancelClaimIDs,
    bulkCancelOpen,
    setBulkCancelOpen,
    bulkEditClaimIDs,
    setBulkEditClaimIDs,
    bulkEditOpen,
    setBulkEditOpen,
  };
};
